/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { useState } from 'react';
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { SubmitButton } from '@g17eco/molecules/button/SubmitButton';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { BasicAlert } from '@g17eco/molecules/alert';
import { getErrorMessage } from '@utils/error';
import { ResponsibilityMode, type StatsDownloadOptions } from '../types';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  /** Caller closes the modal on success; rejections are shown in the modal */
  onDownload: (options: StatsDownloadOptions) => Promise<unknown>;
  isStaff: boolean;
}

export const StatsDownloadModal = ({ isOpen, toggle, onDownload, isStaff }: Props) => {
  const [responsibility, setResponsibility] = useState(ResponsibilityMode.Assigned);
  const [includeStaff, setIncludeStaff] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // Closing mid-download unmounts SubmitButton's spinner, allowing a second export
  const close = isDownloading ? undefined : toggle;

  const handleDownload = () => {
    setIsDownloading(true);
    setErrorMessage('');
    return onDownload({ responsibility, includeStaff })
      .catch((error) => setErrorMessage(getErrorMessage(error)))
      .finally(() => setIsDownloading(false));
  };

  return (
    <Modal isOpen={isOpen} toggle={close} onClosed={() => setErrorMessage('')} backdrop='static'>
      <ModalHeader toggle={close}>Download</ModalHeader>
      <ModalBody>
        <p className='fw-bold mb-2'>Contributor and Verifier columns show</p>
        <FormGroup check>
          <Input
            id='responsibility-assigned'
            type='radio'
            name='responsibility'
            checked={responsibility === ResponsibilityMode.Assigned}
            onChange={() => setResponsibility(ResponsibilityMode.Assigned)}
          />
          <Label check for='responsibility-assigned'>
            Assigned users <span className='text-ThemeTextLight'>(who should answer and verify)</span>
          </Label>
        </FormGroup>
        <FormGroup check>
          <Input
            id='responsibility-answered'
            type='radio'
            name='responsibility'
            checked={responsibility === ResponsibilityMode.Answered}
            onChange={() => setResponsibility(ResponsibilityMode.Answered)}
          />
          <Label check for='responsibility-answered'>
            Last answered by <span className='text-ThemeTextLight'>(who actually answered and verified)</span>
          </Label>
        </FormGroup>
        {isStaff ? (
          <FormGroup check className='mt-3'>
            <Input
              id='include-staff'
              type='checkbox'
              checked={includeStaff}
              onChange={(e) => setIncludeStaff(e.target.checked)}
            />
            <Label check for='include-staff'>
              Include staff users
            </Label>
            <FeatureStability stability='internal' />
          </FormGroup>
        ) : null}
        <BasicAlert type='danger' className='mt-3 mb-0'>
          {errorMessage}
        </BasicAlert>
      </ModalBody>
      <ModalFooter>
        <Button color='link-secondary' onClick={toggle} disabled={isDownloading}>
          Cancel
        </Button>
        <SubmitButton onClick={handleDownload}>Download</SubmitButton>
      </ModalFooter>
    </Modal>
  );
};
